import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight, Calculator, Zap, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui';

const SuccessPage = () => {
  const { user, refreshProfile } = useAuth();

  // Refresh profile to get updated is_pro status
  useEffect(() => {
    if (user) {
      refreshProfile();
    }
  }, [user, refreshProfile]);

  const features = [
    {
      icon: Zap,
      title: 'Unlimited Calculations',
      description: 'No more limits. Calculate as much as you need.',
    },
    {
      icon: FileText,
      title: 'PDF Export',
      description: 'Download your calculations as professional PDFs.',
    },
    {
      icon: Calculator,
      title: 'Advanced Breakdowns',
      description: 'Get detailed cost analysis and material comparisons.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Welcome to Pro! - CalcBuildAI</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-16 px-4">
        <div className="max-w-2xl w-full text-center">
          {/* Success animation */}
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto animate-pulse-soft">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2">
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to Pro!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Your subscription is now active. Enjoy unlimited calculations and premium features.
          </p>

          {/* Features */}
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-md">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="space-y-4">
            <Link to="/">
              <Button size="lg" icon={ArrowRight} iconPosition="right">
                Start Calculating
              </Button>
            </Link>
            <div>
              <Link 
                to="/settings" 
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Manage your subscription →
              </Link>
            </div>
          </div>

          {/* Confirmation details */}
          <div className="mt-12 p-6 bg-gray-50 rounded-xl text-left">
            <h3 className="font-semibold text-gray-900 mb-3">
              What happens next?
            </h3>
            <ul className="space-y-2 text-gray-600 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>You'll receive a confirmation email shortly</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>All Pro features are now unlocked</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Manage your subscription anytime in Settings</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>30-day money-back guarantee applies</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessPage;
